import { Component, Prop, Vue } from 'vue-property-decorator';

import { Offer, Package } from '@/@types/graphql-types';
import { TrackingProviderPropertiesInterface } from '@/helpers/tracking/providers';
import {
	SnowplowClickoutContextGraphql,
	SnowplowContext,
	SnowplowModuleContext,
} from '@/helpers/tracking/providers/snowplow-contexts';

import { ImpressionTrackingEvents } from '@/enums/events';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { ClickoutContentType } from '@/interfaces/snowplow/clickout-context';

import VueObserveVisibility from 'vue-observe-visibility';

Vue.use(VueObserveVisibility);

export type ImpressionTrackingOfferDetails = Pick<
	Offer,
	'id' | 'monetizationType' | 'presentationType' | 'retailPriceValue' | 'currency' | 'lastChangeRetailPriceValue'
> & {
	package: Pick<Package, 'packageId' | 'clearName' | 'technicalName'>;
	isLeavingIconShown?: boolean;
	bundleId?: number;
	bundleTechnicalName?: string;
};

export interface VisibilityChangedPayload {
	isVisible: boolean;
	id: string;
	offer: ImpressionTrackingOfferDetails;
}

export type OnIntersectionCallback = (
	offerId: string,
	offerDetails: ImpressionTrackingOfferDetails,
	isVisible: boolean
) => void;

export const observableOptions = {
	once: false,
	throttle: 300,
	intersection: {
		root: null,
		rootMargin: '0px 0px 0px 0px',
		threshold: 0.5, // 50% visibility of each element
	},
} as const;

@Component({ name: 'UseOfferImpressionTracking' })
export default class UseOfferImpressionTracking extends Vue {
	@Prop({ default: () => [] }) additionalContexts: (SnowplowModuleContext | SnowplowContext)[];

	// uses Set for simplicity and to avoid having duplicates
	// offerIdsToSend can also contain bundle ids to be able to send bundle impression (Promoted BuyBox)
	private offerIdsToSend: Set<string> = new Set();
	private offerDetailsToSend: ImpressionTrackingOfferDetails[] = [];
	private timer: NodeJS.Timeout;

	onIntersection(offerId: string, offerDetails: ImpressionTrackingOfferDetails, isVisible: boolean) {
		if (isVisible && !this.offerIdsToSend.has(offerId)) {
			// if element is new add it to the list
			this.offerIdsToSend.add(offerId);
			this.offerDetailsToSend.push(offerDetails);
		}
	}

	/** Single property if you don't need to override options. */
	onVisibilityChanged(offerId: string, offerDetails: ImpressionTrackingOfferDetails) {
		return {
			...observableOptions,
			callback: (isVisible: boolean) => this.onIntersection(offerId, offerDetails, isVisible),
		};
	}

	private async sendImpressionContext() {
		if (this.offerIdsToSend.size) {
			const properties: TrackingProviderPropertiesInterface = {
				action: 'impression',
				nonInteraction: true,
			};

			await TrackingHelper.trackEvent(ImpressionTrackingEvents.OFFER, properties, [
				...this.offerDetailsToSend.map(offerDetails => getOfferImpressionContext(offerDetails)),
				...this.additionalContexts,
			]);

			this.offerIdsToSend.clear();
			this.offerDetailsToSend = [];
		}
	}

	created() {
		if (process.server) return;

		// send the impressionContext before tab/browser is closed
		if ('onbeforeunload' in window) {
			window.addEventListener('beforeunload', () => this.sendImpressionContext());
		}

		this.timer = setInterval(this.sendImpressionContext, 1000);
	}

	beforeDestroy() {
		if (process.server) return;

		this.sendImpressionContext();
		clearInterval(this.timer);
	}

	render() {
		return this.$scopedSlots.default?.({
			/** Single property if you don't need to override options. */
			onVisibilityChanged: this.onVisibilityChanged,

			/** The exact callback. */
			onIntersection: this.onIntersection,
		});
	}
}

function getOfferImpressionContext(offerDetails: ImpressionTrackingOfferDetails) {
	if (offerDetails?.bundleId) {
		// package.technicalName is available only for bundles
		return TrackingHelper.getPackageContext(
			offerDetails.package.packageId,
			offerDetails.package.technicalName ?? '',
			'bundle',
			offerDetails.bundleId,
			offerDetails.bundleTechnicalName
		);
	}
	const contentType = offerDetails?.isLeavingIconShown ? ClickoutContentType.LeavingSoon : undefined;

	return SnowplowClickoutContextGraphql.fromProviderOffer(offerDetails, contentType);
}

export { getOfferImpressionContext };
